import { useEffect, useRef, useState } from 'react'

// If Turnstile never returns a token (e.g. widget error), release the
// captured submission after this window so the user can retry instead of
// being stuck on a "Verifying…" button forever.
const VERIFICATION_TIMEOUT_MS = 15000

/**
 * Gate a captcha-protected form submission on Cloudflare Turnstile verification.
 *
 * Turnstile is invisible and its token can arrive after the user is ready to
 * submit (slow networks, in-app webviews). Rather than presenting a dead
 * disabled button, this hook:
 *   - captures a submission made before the token exists and replays it
 *     automatically once the token lands, and
 *   - keeps the form locked from the moment a submission starts until the
 *     captcha token is cleared again — the submit thunk does this in its
 *     `.finally`, so the in-flight request can never be dispatched twice.
 *
 * Re-entry guards are ref-backed so two events fired before React re-renders
 * (rapid double-click, click + Enter) can't both get through.
 *
 * @param {string|undefined} token - The current Turnstile token from Redux.
 * @param {Function} onSubmit - Validated-form submit handler (the dispatcher).
 * @returns {{ submit: Function, awaitingVerification: boolean, isSubmitting: boolean }}
 *   `submit` is the form's onSubmit handler; the booleans drive the button.
 */
export default function useDeferredCaptchaSubmit(token, onSubmit) {
  const [awaitingVerification, setAwaitingVerification] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const pendingDataRef = useRef(null)
  // Synchronous source of truth for the guards; state only drives rendering.
  const awaitingRef = useRef(false)
  const submittingRef = useRef(false)
  // `onSubmit` identity changes every render (connect mergeProps), so read it
  // through a ref to keep the replay effect from depending on it.
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit

  const dispatchSubmit = (data) => {
    submittingRef.current = true
    setIsSubmitting(true)
    onSubmitRef.current(data)
  }

  // Form `onSubmit` handler — called with already-validated data.
  const submit = (data) => {
    if (awaitingRef.current || submittingRef.current) return
    if (!token) {
      pendingDataRef.current = data
      awaitingRef.current = true
      setAwaitingVerification(true)
      return
    }
    dispatchSubmit(data)
  }

  // Replay the captured submission as soon as the token arrives.
  useEffect(() => {
    if (!awaitingVerification || !token) return
    const data = pendingDataRef.current
    pendingDataRef.current = null
    awaitingRef.current = false
    setAwaitingVerification(false)
    dispatchSubmit(data)
  }, [awaitingVerification, token])

  // The submit thunk clears the captcha token in its `.finally` when the
  // request settles; treat that token-clear as "submission complete".
  useEffect(() => {
    if (isSubmitting && !token) {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }, [isSubmitting, token])

  // Safety net: don't strand the user on "Verifying…" if the token never lands.
  useEffect(() => {
    if (!awaitingVerification) return undefined
    const timeout = setTimeout(() => {
      pendingDataRef.current = null
      awaitingRef.current = false
      setAwaitingVerification(false)
    }, VERIFICATION_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [awaitingVerification])

  return { submit, awaitingVerification, isSubmitting }
}
